// Settings → Desktop app. In a browser: what the app is and where to get it. Inside the app (emcog-desktop): its
// version and updates, starting with Windows, its address, and Hands on this computer — paired from here in one
// click, paused or stopped from here, with what was done on the computer listed underneath.
import React, { useCallback, useEffect, useState } from 'react';
import { desktopCall, DesktopInfo, HandsStatus, inDesktop } from '../../utils/desktop';
import { useWorkspaceCall } from './Places';
import { useFeatures } from '../../hooks/useFeatures';

const Row: React.FC<{ title: string; hint?: React.ReactNode; children?: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="ws-set-row">
    <div className="ws-set-row-tt"><b>{title}</b>{hint && <span>{hint}</span>}</div>
    {children && <div className="ws-set-row-ctl">{children}</div>}
  </div>
);

const Switch: React.FC<{ on: boolean; label: string; onChange: (on: boolean) => void }> = ({ on, label, onChange }) => (
  <button className={`ws-switch-btn ${on ? 'on' : ''}`} role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}><span /></button>
);

const GetTheApp: React.FC<{ emcogName: string; computer: boolean }> = ({ emcogName, computer }) => {
  const [bundled, setBundled] = useState<boolean | null>(null);
  useEffect(() => { fetch('/downloads/desktop/latest.json', { cache: 'no-store' }).then(r => setBundled(r.status === 200)).catch(() => setBundled(false)); }, []);
  return (
    <div className="ws-set-card">
      <Row title="EmCog for Windows" hint={`${emcogName} in its own window and in the tray — notifications when ${emcogName} reaches out, Ctrl+Shift+Space to bring it up,${computer ? ' Hands built in,' : ''} and it keeps itself up to date.`}>
        {bundled ? <a className="ws-btn ws-primary" href="/downloads/desktop/setup">Download</a> : <span className="ws-faint">{bundled === null ? 'Checking…' : 'Not bundled with this EmCog yet'}</span>}
      </Row>
      {bundled && <Row title="The first time" hint={'Windows may say it doesn\'t recognize the app (it isn\'t code-signed yet). Choose "More info", then "Run anyway". The app asks for this address once.'} />}
    </div>
  );
};

const handsState = (h: HandsStatus): string => !h.paired ? 'Not paired' : h.paused ? 'Paused — nothing is done on this computer' : h.connected ? 'On — connected' : 'Connecting…';

const InTheApp: React.FC<{ emcogName: string; computer: boolean }> = ({ emcogName, computer }) => {
  const call = useWorkspaceCall();
  const [info, setInfo] = useState<DesktopInfo | null>(null);
  const [hands, setHands] = useState<HandsStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { const [i, h] = await Promise.all([desktopCall<DesktopInfo>('app_info'), computer ? desktopCall<HandsStatus>('hands_status') : Promise.resolve(null)]); setInfo(i); setHands(h); }
    catch (e) { setProblem(e instanceof Error ? e.message : String(e)); }
  }, [computer]);
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  const act = async (label: string, run: () => Promise<unknown>) => {
    setBusy(label); setProblem(null);
    try { await run(); } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); }
    setBusy(null); load();
  };
  // Pair this computer with the person's own PIN session, then hand the pairing to the app.
  const pair = () => act('pair', async () => {
    const host = hands?.host || 'This PC';
    const r = await call('/api/computer/pair', 'POST', { name: host });
    await desktopCall('hands_pair', { server: info?.server || r.server, token: r.token, name: host });
  });
  if (!info || (computer && !hands)) return <div className="ws-empty">{problem || 'Opening…'}</div>;
  const u = info.updates;
  return (
    <>
      {problem && <div className="ws-form-error" role="alert">{problem}</div>}
      <div className="ws-set-card">
        <Row title={`EmCog for Windows ${info.version}`} hint={u.pending ? `Version ${u.pending} is downloaded and ready.` : !info.updatesWork ? 'Updates come from an https address; this one is http, so it won\'t update itself.' : (u.last || 'Checks for updates on its own and installs them.')}>
          {u.pending
            ? <button className="ws-btn ws-primary" onClick={() => act('install', () => desktopCall('install_update'))}>Restart to update</button>
            : <button className="ws-btn" disabled={!!busy || !info.updatesWork} onClick={() => act('check', () => desktopCall('check_update'))}>{busy === 'check' ? 'Checking…' : 'Check now'}</button>}
        </Row>
        <Row title="Start with Windows" hint="Starts in the tray when you sign in, so messages reach you.">
          <Switch on={info.autostart} label="Start with Windows" onChange={on => act('autostart', () => desktopCall('set_autostart', { on }))} />
        </Row>
        <Row title="Bring it up" hint={`${info.shortcut} from anywhere. Closing the window keeps ${emcogName} in the tray; Quit is in the tray menu.`} />
        <Row title="Address" hint={info.server || '—'}>
          <button className="ws-btn" onClick={() => act('change', () => desktopCall('change_server'))}>Change</button>
        </Row>
      </div>

      {computer && hands && <>
        <h2 className="ws-sec-h ws-set-sub">Hands on this computer</h2>
        <div className="ws-set-card">
          <Row title={handsState(hands)} hint={hands.paired ? `${emcogName} can use ${hands.name || hands.host} when you ask — every action is listed below and kept in a log on this computer.` : `Let ${emcogName} use this computer when you ask: open things, click, type, read the screen. Risky steps still ask you first, and passwords are never typed.`}>
            {!hands.paired
              ? <button className="ws-btn ws-primary" disabled={busy === 'pair'} onClick={pair}>{busy === 'pair' ? 'Pairing…' : 'Use this computer'}</button>
              : <Switch on={!hands.paused} label="Hands on" onChange={on => act('pause', () => desktopCall('hands_pause', { paused: !on }))} />}
          </Row>
          {hands.note && <Row title="Needs pairing again" hint={hands.note} />}
          {hands.paired && (
            <Row title="Stop using this computer" hint="Forgets the pairing here. Remove it in Connections → Computer too.">
              <button className="ws-btn ws-danger-quiet" onClick={() => act('forget', () => desktopCall('hands_forget'))}>Stop</button>
            </Row>
          )}
        </div>
        {hands.lines.length > 0 && (
          <div className="ws-hands-log" aria-label="What was done on this computer">
            {hands.lines.map((l, i) => <div key={i} className="ws-mono">{l}</div>)}
          </div>
        )}
      </>}
    </>
  );
};

const DesktopSection: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  // Hands is offered only where this EmCog has a computer hand.
  const computer = useFeatures()?.computer === true;
  return inDesktop() ? <InTheApp emcogName={emcogName} computer={computer} /> : <GetTheApp emcogName={emcogName} computer={computer} />;
};

export default DesktopSection;
